"use client";

import { useState, type ComponentProps, type CSSProperties } from "react";
import { commonUrl } from "@/features/media/mediaUrl";
import { cn } from "@/lib/cn";
import type { ProjectBasemap } from "@/types";
import { useSelectedBasemap } from "../useBasemaps";

/**
 * Media URL for a basemap thumbnail.
 *
 * Basemap icons are addressed without a tenant, since the same picker is shown
 * for every project — the legacy app did the same.
 */
function basemapIconUrl(basemap: ProjectBasemap): string | null {
  return commonUrl(basemap.icon);
}

interface BasemapButtonProps extends ComponentProps<"button"> {
  isActive: boolean;
  icon: string | null;
}

function BasemapButton({ isActive, icon, ...props }: BasemapButtonProps) {
  return (
    <button
      type="button"
      // Set as a variable so the class list stays static and Tailwind can see it.
      style={icon ? ({ "--icon": `url("${icon}")` } as CSSProperties) : undefined}
      className={cn(
        "pointer-events-auto h-[55px] w-[55px] overflow-hidden rounded-full",
        "border-[3px] border-white bg-cover bg-center",
        "dark:border-dark-color dark:border-opacity-80",
        isActive && "border-primary",
        // A tenant that hasn't uploaded a thumbnail would otherwise get a
        // transparent hole rather than a button.
        icon ? "bg-[image:var(--icon)]" : "bg-neutral-200"
      )}
      {...props}
    />
  );
}

export function BasemapSelector() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { basemap: selectedBasemap, basemaps, setBasemap } = useSelectedBasemap();

  // A picker with one option is a button that does nothing.
  if (basemaps.length < 2 || !selectedBasemap) return null;

  const selectBasemap = (id: string | undefined) => {
    if (id && selectedBasemap.id !== id) {
      void setBasemap(id);
      setIsDropdownOpen(false);
    }
  };

  return (
    <div className="mb-10 ml-4 flex flex-col gap-4">
      {isDropdownOpen && (
        <div
          className={cn(
            "flex w-max flex-wrap gap-4 rounded-70 bg-gray-50 bg-opacity-40 px-5 py-3.5",
            "backdrop-blur dark:bg-dark-color dark:bg-opacity-20"
          )}
        >
          {basemaps.map((basemap) => (
            <BasemapButton
              key={basemap.id}
              isActive={selectedBasemap.id === basemap.id}
              icon={basemapIconUrl(basemap)}
              title={basemap.id}
              onClick={() => selectBasemap(basemap.id)}
            />
          ))}
        </div>
      )}

      <BasemapButton
        icon={basemapIconUrl(selectedBasemap)}
        title="Active Basemap"
        isActive={isDropdownOpen}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      />
    </div>
  );
}
